<template>
  <el-dialog
    :model-value="visible"
    title="ダイナミック値ビルダー"
    width="700px"
    @close="onCancel"
  >
    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-width="0"
      @submit.prevent="onSave"
    >
      <div>
        <div
          v-for="(item, idx) in valueList"
          :key="item.id"
          class="value-block"
        >
          <div class="value-block-header">
            <span style="margin-right: 8px; font-weight: 500">値</span>
            <el-form-item
              :prop="`values.${idx}.value`"
              :rules="[
                { required: true, message: '値は必須です', trigger: 'blur' }
              ]"
            >
              <el-input
                v-model="item.value"
                size="small"
                height="32px"
                style="width: 240px; height: 32px"
              />
            </el-form-item>
            <el-button
              type="danger"
              size="small"
              plain
              circle
              style="margin-left: auto"
              @click="removeValue(idx)"
            >
              <el-icon><DeleteFilled /></el-icon>
            </el-button>
          </div>
          <div
            v-if="duplicateValues.includes(String(item.value))"
            class="input-error-message"
          >
            重複した値があります！
          </div>
          <div style="margin-bottom: 4px; font-weight: 500">条件</div>
          <DynamicBuilder
            :group="item.group"
            :form-ref="formRef"
            :form-prefix="`values.${idx}.group`"
          />
        </div>
        <el-button type="primary" @click="addValue" style="margin-top: 8px">
          値を追加
        </el-button>
      </div>
    </el-form>
    <template #footer>
      <el-button @click="onCancel">キャンセル</el-button>
      <el-button
        type="primary"
        :disabled="duplicateValues.length > 0"
        @click="onSave"
      >
        保存
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import DynamicBuilder from './DynamicBuilder.vue';
  import { ElIcon } from 'element-plus';
  import { DeleteFilled } from '@element-plus/icons-vue';
  import type { FormInstance, FormRules } from 'element-plus';

  type DynamicRule = { field: string; operator: string; value: string };
  type DynamicGroup = {
    combinator: 'AND' | 'OR';
    rules: Array<DynamicRule | DynamicGroup>;
  };

  const props = defineProps<{
    visible: boolean;
    group: Record<number | string, DynamicGroup>;
  }>();
  const emit = defineEmits(['save', 'cancel']);

  interface ValueItem {
    id: number;
    value: number | string;
    group: DynamicGroup;
  }

  const formRef = ref<FormInstance>();
  const valueList = ref<ValueItem[]>([]);
  const wasVisible = ref(false);

  // Form data for validation
  const formData = computed(() => ({
    values: valueList.value
  }));

  // Form rules
  const formRules: FormRules = {
    values: [
      {
        type: 'array',
        required: true,
        message: '少なくとも1つの値を追加してください',
        trigger: 'change'
      }
    ]
  };

  watch(
    () => props.visible,
    (val) => {
      if (val && !wasVisible.value) {
        // chỉ đồng bộ khi dialog vừa mở
        valueList.value = Object.entries(props.group).map(([k, v]) => ({
          id: Number(k),
          value: k,
          group: v
        }));
        wasVisible.value = true;
      } else if (!val) {
        wasVisible.value = false;
      }
    },
    { immediate: true }
  );

  const addValue = () => {
    let next = 1;
    const used = new Set(valueList.value.map((v) => v.value));
    while (used.has(String(next))) next++;
    valueList.value.push({
      id: Date.now() + Math.random(),
      value: next,
      group: { combinator: 'AND', rules: [] }
    });
  };

  const removeValue = (idx: number) => {
    valueList.value.splice(idx, 1);
  };

  const duplicateValues = computed(() => {
    const seen = new Set<string>();
    const dups: string[] = [];
    for (const item of valueList.value) {
      const value = String(item.value);
      if (seen.has(value)) dups.push(value);
      else seen.add(value);
    }
    return dups;
  });

  const onSave = async () => {
    if (duplicateValues.value.length > 0) return;

    try {
      await formRef.value?.validate();
      const result: Record<string, DynamicGroup> = {};
      valueList.value.forEach((item) => {
        if (item.value !== undefined && item.value !== null) {
          result[isNaN(Number(item.value)) ? item.value : Number(item.value)] =
            item.group;
        }
      });
      emit('save', result);
    } catch (error) {
      console.log('Form validation failed:', error);
    }
  };

  const onCancel = () => emit('cancel');
</script>

<style scoped lang="scss">
  .input-error-message {
    color: #f56c6c;
    font-size: 12px;
    margin: 2px 0 0 4px;
    padding: 0;
    line-height: 1.2;
    position: relative;
    z-index: 2;
  }
  .value-block {
    border: 1px solid #e0e0e0;
    padding: 12px;
    margin-bottom: 16px;
    background: #fafbfc;
  }
  .value-block-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    justify-content: space-between;
  }
  .builder-group {
    border: 1px solid #e0e0e0;
    padding: 12px;
    margin-bottom: 10px;
    background: #fafbfc;
  }
  .builder-group-header {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }
  .builder-rule,
  .builder-nested-group {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
</style>
